"use client";

import { useLazyQuery, useQuery } from "@apollo/client";
import {
  Button,
  Input,
  LiquidPillDropdown,
  LiquidPillGroup,
  Modal,
  ModalButtons,
} from "components/ui/v2";
import type { LiquidPillDropdownItem } from "components/ui/v2/liquid-pill-dropdown";
import type { LiquidPillGroupItem } from "components/ui/v2/liquid-pill-group";
import { MaintainerDataTableV2 } from "components/ui/v2/maintainer/maintainer-data-table-v2";
import { IColumn } from "components/ui/v2/maintainer/types";
import {
  commonTypes,
  getIconByType,
} from "components/ui/v2/shared/bundle/profile/CertificationCard";
import SimpUserBadge from "components/ui/v2/shared/bundle/profile/SimpUserBadge";
import { gqlUser, gqlVendor } from "gql";
import {
  AlertTriangle,
  Award,
  BarChart2,
  Bell,
  Clock,
  Download,
  Eye,
  MoreHorizontal,
  RefreshCw,
  Search,
  Users,
  X,
} from "lucide-react";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  ECertificationType,
  IUser,
  IUserCertification,
} from "types/User.types";
import {
  IVendorCertificationExpiryStats,
  IVendorUserCertifications,
  VendorCertificationExpiryStatus,
} from "types/Vendor.types";
import useDebounce from "utils/hooks/useDebounce";
import { HomeEmptyStateV2 } from "./HomeEmptyStateV2";
import { HomeLoadingStateV2 } from "./HomeLoadingStateV2";
import { HomeMetricCardV2 } from "./HomeMetricCardV2";
import { HomeSectionCardV2 } from "./HomeSectionCardV2";
import { HomeTabsV2 } from "./HomeTabsV2";
import {
  NOTIFY_GROUP_FORM_ID,
  NotifyGroupModalContentV2,
} from "./NotifyGroupModalContentV2";
import {
  NOTIFY_USER_FORM_ID,
  NotifyUserModalContentV2,
} from "./NotifyUserModalContentV2";
import { UserCertificationsModalContentV2 } from "./UserCertificationsModalContentV2";

type ExpiryFilterValue = "" | VendorCertificationExpiryStatus;

type NotifyTarget = {
  user: IUser;
  initialText?: string;
};

export function DashboardCertificationsPanelV2() {
  const [search, setSearch] = useState("");
  const debouncedSearchTerm = useDebounce(search, 1000);
  const [active, setActive] = useState<string>("certifications");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [limit] = useState<number>(10);
  const [expiryStatus, setExpiryStatus] = useState<ExpiryFilterValue>("");
  const [displayableData, setDisplayableData] = useState<
    IVendorUserCertifications[]
  >([]);
  const [certificationsModalUser, setCertificationsModalUser] =
    useState<IUser | null>(null);
  const [notifyTarget, setNotifyTarget] = useState<NotifyTarget | null>(null);
  const [notifyGroupOpen, setNotifyGroupOpen] = useState(false);
  const { t } = useTranslation("common");

  const handleOpenCertificationsDetails = (userCertificated: IUser) => {
    setCertificationsModalUser(userCertificated);
  };

  const { data } = useQuery(gqlVendor.queries.CERTIFICATION_STATS, {
    fetchPolicy: "network-only",
    variables: {},
  });

  const { data: typesData } = useQuery(
    gqlVendor.queries.CERTIFICATION_TYPES_STATS,
    {
      fetchPolicy: "network-only",
      variables: {},
    },
  );

  const { data: expiryStatsData } = useQuery<{
    vendorCertificationExpiryStats: IVendorCertificationExpiryStats;
  }>(gqlVendor.queries.VENDOR_CERTIFICATION_EXPIRY_STATS, {
    fetchPolicy: "network-only",
  });

  const expiryStats = expiryStatsData?.vendorCertificationExpiryStats;

  const [
    vendorUsersCertificationsPaginated,
    { loading: vendorUsersCertificationsLoading },
  ] = useLazyQuery(gqlVendor.queries.GET_VENDOR_USER_CERTIFICATIONS_PAGINATED, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    onCompleted: (response) => {
      if (!response) return;
      setDisplayableData(
        response.vendorUsersCertificationsPaginated.userCertifications,
      );
      setTotal(response.vendorUsersCertificationsPaginated.total);
    },
  });

  const buildInput = useCallback(
    (pageNum: number, searchText: string, status: ExpiryFilterValue) => ({
      offset: (pageNum - 1) * limit,
      limit,
      searchText,
      ...(status ? { expiryStatus: status } : {}),
    }),
    [limit],
  );

  const fetchCertifications = useCallback(
    (pageNum: number, searchText: string, status: ExpiryFilterValue) => {
      void vendorUsersCertificationsPaginated({
        variables: {
          vendorUserCertificationsInput: buildInput(
            pageNum,
            searchText,
            status,
          ),
        },
      });
    },
    [buildInput, vendorUsersCertificationsPaginated],
  );

  const buildRenewMessage = (user: IUser) => {
    const name = [user.name, user.lastname].filter(Boolean).join(" ").trim();
    return `${t("Hola")}${name ? ` ${name}` : ""},\n\n${t(
      "Te pedimos renovar tus certificaciones próximas a vencer o ya vencidas.",
    )}`;
  };

  const handleUserNotify = (user: IUser, initialText?: string) => {
    setNotifyTarget({ user, initialText });
  };

  const getActionItems = (
    rowData: IVendorUserCertifications,
  ): LiquidPillDropdownItem[] => {
    const items: LiquidPillDropdownItem[] = [];
    const expiringSoon = rowData.quantityExpiringSoon ?? 0;
    const canRenew =
      (rowData.quantityOutOfDate ?? 0) > 0 || expiringSoon > 0;

    if (rowData.quantity > 0) {
      items.push({
        value: "view",
        label: t("Ver"),
        icon: Eye,
        variant: "indigo",
      });
    }

    items.push({
      value: "notify",
      label: t("Enviar notificación"),
      icon: Bell,
      variant: "sky",
    });

    if (canRenew) {
      items.push({
        value: "renew",
        label: t("Renovar"),
        icon: RefreshCw,
        variant: "gold",
      });
    }

    return items;
  };

  const handleActionSelect = (
    value: string,
    rowData: IVendorUserCertifications,
  ) => {
    if (value === "view") {
      handleOpenCertificationsDetails(rowData.user);
      return;
    }
    if (value === "notify") {
      handleUserNotify(rowData.user);
      return;
    }
    if (value === "renew") {
      handleUserNotify(rowData.user, buildRenewMessage(rowData.user));
    }
  };

  const columns: IColumn<IVendorUserCertifications>[] = [
    {
      dataKey: "user",
      header: t("Nombre"),
      width: 280,
      sortable: true,
      customCell: ({ rowData }) => (
        <div className="flex items-center gap-1">
          <SimpUserBadge
            user={rowData.user}
            size={22}
            showRole
            previewProfile
          />
        </div>
      ),
    },
    {
      dataKey: "user",
      header: t("Tipos"),
      width: 200,
      customCell: ({ rowData }) => {
        const types = Array.from(
          new Set(
            (rowData.user?.certifications ?? [])
              .map((cert: IUserCertification) =>
                cert.type &&
                commonTypes.includes(cert.type as ECertificationType)
                  ? (cert.type as ECertificationType)
                  : null,
              )
              .filter((type): type is ECertificationType => Boolean(type)),
          ),
        );
        return (
          <div className="flex gap-2">
            {types.map((type) => (
              <div key={type}>{getIconByType(type, 16)}</div>
            ))}
          </div>
        );
      },
    },
    {
      dataKey: "quantity",
      header: t("Activas"),
      width: 100,
      sortable: true,
    },
    {
      dataKey: "quantityExpiringSoon",
      header: t("Por vencer"),
      width: 120,
      sortable: true,
      customCell: ({ rowData }) => (
        <span>{rowData.quantityExpiringSoon ?? 0}</span>
      ),
    },
    {
      dataKey: "quantityOutOfDate",
      header: t("Vencidas"),
      width: 120,
      sortable: true,
    },
    {
      dataKey: "unix",
      header: t("Última actualización"),
      width: 200,
      sortable: true,
      customCell: ({ rowData }) => (
        <div className="space-between flex">
          {rowData.lastUpload
            ? moment(rowData.lastUpload).format("DD MMMM YYYY")
            : null}
        </div>
      ),
    },
    {
      dataKey: "user",
      header: t("Acciones"),
      headerStyle: { textAlign: "center" },
      cellStyle: {
        display: "grid",
        placeItems: "center",
        justifyContent: "center",
      },
      fixed: "right",
      width: 92,
      customCell: ({ rowData }) => (
        <LiquidPillDropdown
          items={getActionItems(rowData)}
          onSelect={(value) => handleActionSelect(value as string, rowData)}
          icon={<MoreHorizontal className="h-4 w-4" />}
          size="sm"
          collapsedShape="circle"
          reactive
          variant="slate"
          aria-label={t("Opciones")}
        />
      ),
    },
  ];

  const [getReport] = useLazyQuery(gqlUser.queries.USER_REPORT, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ createUserReportXLS }) => {
      const link = document.createElement("a");
      link.href = createUserReportXLS;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: ({ message }) => {
      toast.error(message);
    },
    variables: {
      type: "certifications",
    },
  });

  const handleDownloadReport = async () => {
    try {
      toast.promise(
        getReport({
          variables: {
            type: "certifications",
          },
        }),
        {
          loading: t("Descargando reporte"),
          success: t("Reporte descargado"),
          error: t("Error al descargar reporte"),
        },
      );
    } catch (error) {
      console.error("🚀 ~ error", error);
    }
  };

  const handleNotifyGroup = () => {
    setNotifyGroupOpen(true);
  };

  const handleChangePage = async (currentPage: number) => {
    const response = await vendorUsersCertificationsPaginated({
      variables: {
        vendorUserCertificationsInput: buildInput(
          currentPage,
          search,
          expiryStatus,
        ),
      },
    });

    const users = response?.data?.["vendorUsersCertificationsPaginated"];
    if (!users) return;
    setTotal(users.total);
    setDisplayableData(users.userCertifications);
    setPage(currentPage);
  };

  const handleExpiryFilterChange = (value: string) => {
    const next = (value === "all" ? "" : value) as ExpiryFilterValue;
    setExpiryStatus(next);
    setPage(1);
    fetchCertifications(1, search, next);
  };

  const expiryFilterItems: LiquidPillGroupItem[] = useMemo(
    () => [
      { value: "all", label: t("Todas"), variant: "slate" },
      {
        value: "expiringSoon",
        label: t("Por vencer"),
        icon: <Clock className="h-4 w-4" />,
        variant: "gold",
      },
      {
        value: "expired",
        label: t("Vencidas"),
        icon: <AlertTriangle className="h-4 w-4" />,
        variant: "rose",
      },
      {
        value: "active",
        label: t("Vigentes"),
        icon: <Award className="h-4 w-4" />,
        variant: "emerald",
      },
    ],
    [t],
  );

  useEffect(() => {
    fetchCertifications(1, "", "");
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPage(1);
    fetchCertifications(1, search, expiryStatus);
    // Debounced search only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  return (
    <>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <HomeMetricCardV2
          title={t("Activas")}
          value={expiryStats?.active ?? 0}
          filled
          icon={<Award className="h-4 w-4 text-emerald-500" />}
        />
        <HomeMetricCardV2
          title={t("Por vencer 30d")}
          value={expiryStats?.expiringSoon ?? 0}
          filled
          icon={<Clock className="h-4 w-4 text-amber-500" />}
        />
        <HomeMetricCardV2
          title={t("Vencidas")}
          value={expiryStats?.expired ?? 0}
          filled
          icon={<AlertTriangle className="h-4 w-4 text-red-500" />}
        />
        <HomeMetricCardV2
          title={t("Total colaboradores")}
          value={total || 0}
          filled
          icon={<Users className="h-4 w-4 text-blue-500" />}
        />
      </div>

      <div className="mb-6">
        <HomeSectionCardV2>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <HomeTabsV2
                value={active}
                onChange={setActive}
                tone="light"
                items={[
                  {
                    key: "certifications",
                    label: t("Búsqueda"),
                    icon: <Search className="h-4 w-4 mr-2 inline-block" />,
                  },
                  {
                    key: "stats",
                    label: t("Estadísticas"),
                    icon: <BarChart2 className="h-4 w-4 mr-2 inline-block" />,
                  },
                ]}
              />

              <div className="flex gap-3">
                <Button
                  variant="primary"
                  onClick={handleNotifyGroup}
                  size="lg"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  {t("Notificar grupo")}
                </Button>
                <Button
                  variant="secondary"
                  onClick={handleDownloadReport}
                  size="lg"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t("Descargar")}
                </Button>
              </div>
            </div>

            {active === "certifications" && (
              <div className="flex w-full flex-col items-center gap-3 sm:flex-row sm:items-center sm:flex-wrap">
                <div className="w-full sm:w-80">
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("Buscar usuario")}
                  />
                </div>
                <LiquidPillGroup
                  items={expiryFilterItems}
                  activeValue={expiryStatus || "all"}
                  onChange={handleExpiryFilterChange}
                  size="sm"
                />
                {search && (
                  <Button
                    variant="secondary"
                    onClick={() => setSearch("")}
                    size="lg"
                  >
                    <X className="h-4 w-4 mr-2" />
                    {t("Limpiar búsqueda")}
                  </Button>
                )}
              </div>
            )}

            {search && (
              <p className="text-sm text-slate-600 dark:text-zinc-400">
                {t("Resultados para")}{" "}
                <span className="font-semibold text-blue-600 dark:text-blue-400">
                  &quot;{search}&quot;
                </span>
              </p>
            )}
          </div>
        </HomeSectionCardV2>
      </div>

      <HomeSectionCardV2>
        {active === "certifications" && (
          <>
            {vendorUsersCertificationsLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <HomeLoadingStateV2 />
                <p className="mt-4 text-sm text-slate-600 dark:text-zinc-400">
                  {t("Cargando certificaciones")}
                </p>
              </div>
            ) : displayableData.length === 0 ? (
              <HomeEmptyStateV2
                icon={<Award className="h-7 w-7" />}
                title={
                  search || expiryStatus
                    ? t("No se encontraron usuarios")
                    : t("No hay certificaciones aún")
                }
                description={
                  search || expiryStatus
                    ? t("Intenta ajustar tus criterios de búsqueda")
                    : t("Las certificaciones aparecerán aquí cuando se creen")
                }
              />
            ) : (
              <MaintainerDataTableV2<IVendorUserCertifications>
                data={displayableData}
                columns={columns}
                loading={vendorUsersCertificationsLoading}
                pagination={{
                  currentPage: page,
                  totalItems: total,
                  pageSize: limit,
                  onPageChange: handleChangePage,
                  totalPages: Math.ceil(total / limit) || 0,
                  perPageLabel: `${t("Certificaciones por página")}: ${limit}`,
                }}
              />
            )}
          </>
        )}

        {active === "stats" && (
          <div className="space-y-8">
            <div>
              <h3 className="mb-4 text-sm font-semibold text-slate-900 dark:text-zinc-100">
                {t("Certificaciones por Certificador")}
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[...(data?.vendorCertificationsStats ?? [])]
                  ?.sort((a, b) => b.quantity - a.quantity)
                  .map((cert) => (
                    <HomeMetricCardV2
                      key={cert.certificationType}
                      title={cert.certificationType}
                      value={cert.quantity}
                      filled
                      icon={getIconByType(cert.certificationType)}
                    />
                  ))}
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-sm font-semibold text-slate-900 dark:text-zinc-100">
                {t("Certificaciones por Tipo")}
              </h3>
              <div className="space-y-2">
                {typesData?.vendorTypeCertificationsStats?.map((cert) => (
                  <div
                    key={cert.typeCertification}
                    className="flex items-center justify-between rounded-lg border border-slate-200/70 bg-slate-50/80 px-4 py-3 dark:border-zinc-800 dark:bg-zinc-900/50"
                  >
                    <span className="font-medium text-slate-900 dark:text-zinc-100">
                      {t(cert.typeCertification)}
                    </span>
                    <span className="rounded-full bg-blue-100 px-3 py-1 text-sm font-semibold text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      {cert.quantity}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </HomeSectionCardV2>

      <Modal
        open={Boolean(certificationsModalUser)}
        onClose={() => setCertificationsModalUser(null)}
        title={t("Certificaciones")}
        size="lg"
        panelClassName="max-h-[min(88vh,680px)]"
        headerClassName="px-4 py-2.5 sm:px-5 sm:py-3"
        bodyClassName="px-4 py-3 sm:px-5 sm:py-3.5"
        footer={
          <ModalButtons
            compact
            cancelLabel={t("Cerrar")}
            onCancel={() => setCertificationsModalUser(null)}
            submitLabel={t("Listo")}
            onSubmit={() => setCertificationsModalUser(null)}
          />
        }
      >
        {certificationsModalUser ? (
          <UserCertificationsModalContentV2
            userCertificated={certificationsModalUser}
            onClose={() => setCertificationsModalUser(null)}
            hideHeader
          />
        ) : null}
      </Modal>

      <Modal
        open={Boolean(notifyTarget)}
        onClose={() => setNotifyTarget(null)}
        title={t("Enviar notificación")}
        size="md"
        footer={
          <ModalButtons
            compact
            onCancel={() => setNotifyTarget(null)}
            cancelLabel={t("Cerrar")}
            submitLabel={t("Enviar")}
            submitButtonProps={{
              form: NOTIFY_USER_FORM_ID,
              type: "submit",
            }}
          />
        }
      >
        {notifyTarget && (
          <NotifyUserModalContentV2
            key={`${notifyTarget.user.id}-${notifyTarget.initialText ?? ""}`}
            user={notifyTarget.user}
            initialText={notifyTarget.initialText}
            onClose={() => setNotifyTarget(null)}
            hideHeader
            hideFooter
          />
        )}
      </Modal>

      <Modal
        open={notifyGroupOpen}
        onClose={() => setNotifyGroupOpen(false)}
        title={t("Notificar grupo")}
        size="md"
        footer={
          <ModalButtons
            compact
            onCancel={() => setNotifyGroupOpen(false)}
            cancelLabel={t("Cerrar")}
            submitLabel={t("Notificar ahora")}
            submitButtonProps={{
              form: NOTIFY_GROUP_FORM_ID,
              type: "submit",
            }}
          />
        }
      >
        <NotifyGroupModalContentV2
          onClose={() => setNotifyGroupOpen(false)}
          hideHeader
          hideFooter
        />
      </Modal>
    </>
  );
}
